import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import InfoOutline from "@mui/icons-material/InfoOutline";
import Warning from "@mui/icons-material/Warning";
import { alpha, Box, Chip, Tooltip, Typography, useTheme } from "@mui/material";
import { useMemo, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLexicalContext } from "../../lexical-context";
import { MEDIAN_MERGE_TAG_LENGTH } from "../../utils/use-get-character-count";

export const SmsCharacterCount: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    text: { characterCount },
  } = useLexicalContext();

  const {
    icon,
    backgroundColor,
    color,
  }: { icon: ReactNode; backgroundColor: string; color?: string } = useMemo(() => {
    if (!characterCount || characterCount === 0)
      return {
        icon: <InfoOutline fontSize="small" />,
        backgroundColor: theme.palette.background.default,
      };

    if (characterCount <= 500)
      return {
        icon: <CheckCircleOutline fontSize="small" />,
        backgroundColor: alpha(theme.palette.success.main, 0.12),
      };

    if (characterCount <= 1000)
      return {
        icon: <InfoOutline fontSize="small" />,
        backgroundColor: alpha(theme.palette.warning.main, 0.12),
      };

    if (characterCount < 1500)
      return { icon: <InfoOutline fontSize="small" />, backgroundColor: "warning.main" };

    return {
      icon: <Warning fontSize="small" />,
      backgroundColor: "error.main",
      color: theme.palette.error.contrastText,
    };
  }, [characterCount, theme.palette]);

  return (
    <Tooltip
      title={
        <>
          <Typography variant="body2" fontSize={10} fontWeight={500} sx={{ mb: 1 }}>
            {t(
              "The ideal SMS character count is around 320 characters. Messages over 1500 characters cannot be sent.",
            )}
          </Typography>
          <Typography variant="body2" fontSize={10} fontWeight={500} sx={{ mb: 1 }}>
            {t(
              "Some special characters, such as emojis or accented letters, count as multiple characters.",
            )}
          </Typography>
          <Typography variant="body2" fontSize={10} fontWeight={500}>
            {t("Merge tags count as {{medianMergeTag}} characters.", {
              medianMergeTag: MEDIAN_MERGE_TAG_LENGTH,
            })}
          </Typography>
        </>
      }
    >
      <Box sx={{ p: 1 }}>
        <Chip
          size="small"
          icon={icon}
          label={`${characterCount ?? 0}/${1500}`}
          sx={{
            minWidth: 120,
            position: "relative",
            "&.MuiChip-root": {
              backgroundColor,
            },
            justifyContent: "flex-start",
            "& .MuiChip-icon": {
              position: "absolute",
              left: 5,
              margin: 0,
              color,
            },
            "& .MuiChip-label": {
              paddingLeft: "25px",
              textAlign: "center",
              flex: 1,
              color,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};
